// supervisor-loop.ts
//
// One tick of the §6.1 control loop: next ready ticket -> attempt start +
// baseline -> attempt cycle -> forensics -> route -> ticket_state_changed +
// attempt_finished -> reset after a non-done attempt -> recovery. One `await`
// chain per call; callers own the poll cadence (no setInterval/setTimeout).
import { randomUUID } from "node:crypto";
import { readDecisionLedger } from "./decision-ledger-projection.js";
import { knownValuesForControlRoot } from "./mission-secrets-test-env-loader.js";
import { materializeRecoveryAfterStop } from "./recovery-after-attempt-stop.js";
import { renderMissionDecisionLedgerSection } from "./run-command-prompt-mission-decision-ledger-section.js";
import { renderPreviousRejectionSection } from "./run-command-prompt-previous-rejection-section.js";
import { renderRedTestsSection } from "./run-command-prompt-red-tests-section.js";
import { nextReadyTicket } from "./scheduler.js";
import { runAttemptCycle } from "./supervisor-attempt-cycle.js";
import {
  deadlineRateLimitStreakFor,
  quotaBackoffIndexFor,
  readAttemptStateFile,
  retryStateFor,
  withDeadlineRateLimitStreakAfterRoute,
} from "./supervisor-attempt-state.js";
import { archiveForensicsForVerdict } from "./supervisor-forensics-archival.js";
import { journalAttemptStart } from "./supervisor-loop-attempt-start.js";
import { finalizeAttemptAfterRoute } from "./supervisor-loop-post-route-finalize.js";
import {
  manifestPathsForTicket,
  type SupervisorTickInput,
  type SupervisorTickResult,
} from "./supervisor-loop-types.js";
import { routeAttemptOutcome } from "./supervisor-outcome-router.js";
import { resetWorktreeAfterNonDoneRoute } from "./supervisor-post-route-worktree-reset.js";
import { runPreDispatchChecks } from "./supervisor-pre-dispatch-checks.js";
import {
  loadRecipeBashGrants,
  renderRecipeBashPromptSection,
} from "./verification-recipe-bash-permission-rules.js";

// Types split to supervisor-loop-types.ts (bước 0a, phase 03) — re-exported
// so `import … from "./supervisor-loop.js"` keeps working for every existing
// importer (supervisor-pre-dispatch-checks.ts, supervisor-run-until-idle-
// types.ts, supervisor-candidate-review-dispatch.ts).
export type { SupervisorTickInput, SupervisorTickResult } from "./supervisor-loop-types.js";

/**
 * Runs exactly one attempt for the scheduler's next ready ticket, or returns
 * `idle` when nothing is ready (busy ticket in flight, or DAG exhausted).
 */
export async function runOneSupervisorTick(
  input: SupervisorTickInput,
): Promise<SupervisorTickResult> {
  const now = input.now ?? (() => new Date());
  const ticket = nextReadyTicket(input.projection);
  if (ticket === undefined) return { kind: "idle" };

  const preflight = await runPreDispatchChecks({
    controlRoot: input.controlRoot,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    ticket,
    g2PlanReadiness: input.g2PlanReadiness,
  });
  if (preflight !== undefined) return preflight;

  const attemptState = await readAttemptStateFile(input.controlRoot);
  const priorRetryState = retryStateFor(attemptState, ticket.id);
  const attemptNo = priorRetryState.physical_runs + 1;
  // Ladder position for THIS ticket's next quota sleep, if it hits one —
  // read here (not hardcoded 0) so consecutive quota retries on the same
  // ticket climb backoff_min[1], [2], ... instead of replaying step 0
  // forever (§18/§6.4 "backoff 15→30→60 phút").
  const priorBackoffIndex = quotaBackoffIndexFor(attemptState, ticket.id);

  // D08 (dogfood vòng 4, Bug D fix): run_id + input_hash generated inside
  // journalAttemptStart, before dispatch, so both are journaled on
  // attempt_started itself (supervisor-loop-attempt-start.ts, split —
  // review 260909-p6-m2 INFO).
  // Dogfood F-2: the ticket's own approved recipe becomes a Bash grant; the
  // prompt says so, and is journaled (input_hash) with that section included.
  const recipeBash = loadRecipeBashGrants(input.repoDir, ticket);
  if (attemptNo === 1) {
    for (const line of recipeBash.skipped)
      input.notify?.(`${ticket.id}: không cấp Bash cho ${line}`);
  }
  // 06d R6 (F-16, E19): a retry says why the last attempt was refused, masked
  // with the CURRENT registry; before the recipe section, which stays last.
  const knownValues = attemptNo >= 2 ? await knownValuesForControlRoot(input.controlRoot) : [];
  // 06f R4 (F-32, E30): both review sessions get the mission's settled choices
  // (no questions; ledger already known-values masked), before the E19 section.
  const ledgerSection =
    ticket.kind === "review"
      ? renderMissionDecisionLedgerSection(await readDecisionLedger(input.controlRoot))
      : "";
  const prompt =
    input.buildPrompt(ticket) +
    ledgerSection +
    // RED tests from the done testcase(s) this implement/fix turns green; "" for other adapters.
    renderRedTestsSection(ticket, input.projection.tickets, input.projection.ticketStatus) +
    renderPreviousRejectionSection(input.store.state, ticket.id, attemptNo, knownValues) +
    renderRecipeBashPromptSection(recipeBash);
  const attemptStartedAt = now();
  const started = await journalAttemptStart({ tickInput: input, ticket, attemptNo, prompt });

  const cycle = await runAttemptCycle({
    projection: input.projection,
    ticket,
    missionId: input.missionId,
    missionWorkspace: input.missionWorkspace,
    repoRoot: input.repoDir,
    attemptNo,
    runtimeLimitsForKind: input.runtimeLimitsForKind,
    sessionDeps: input.sessionDeps,
    prompt,
    recipeBashRules: recipeBash.rules,
    workerModel: input.workerModel,
    ...started, // runId + sessionId (P1 R2) journaled on attempt_started
  });

  // MUST run before this function returns — the NEXT tick's
  // `recoverOwnedCheckout` (supervisor-pre-dispatch-checks.ts) would erase
  // the exact diff forensics needs to archive for a contract_error/recovering
  // verdict (§9.4). SOL-05: the CONTROL root — `attempts/` is read there
  // (log retention, orphan reclaim); inside the worktree the next tick's
  // `git clean -fd` erased it and a later bootstrap commit swept it in.
  await archiveForensicsForVerdict({
    controlRoot: input.controlRoot,
    repoDir: input.repoDir,
    ticketId: ticket.id,
    attemptNo,
    cycle,
  });

  const route = await routeAttemptOutcome({
    cycle,
    retryState: priorRetryState,
    retryConfig: input.retryConfig,
    quotaConfig: input.quotaConfig,
    quotaStatePath: `${input.controlRoot}/quota-sleep.json`,
    // B4: control root, KHÔNG phải missionWorkspace — gate đọc interview.json ở
    // cùng root này (xem `controlRoot` trong supervisor-pre-dispatch-checks.ts).
    // Verifier thì nhận `controlRoot: input.missionWorkspace` — root KHÁC; nhầm
    // hai cái là câu hỏi ghi ra chỗ không ai gác.
    // Không ghi số dòng ở đây: hai số cũ (`:107`, `:167`) đều trỏ sai sau một
    // lần tách file, đúng loại chú thích tự mục ruỗng.
    controlRoot: input.controlRoot,
    backoffIndex: priorBackoffIndex,
    now: now(),
    store: input.store,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    manifestPaths: manifestPathsForTicket(ticket),
    missionId: input.missionId,
    runId: cycle.kind === "verified" ? cycle.runId : randomUUID(),
    ticketId: ticket.id,
    ticketType: ticket.kind,
    ticketScope: ticket.slice_id,
    ticketSubject: ticket.title,
    ticketStatus: input.projection.ticketStatus,
    beforeSleep: input.beforeSleep,
    notify: input.notify,
    deadlineRateLimitStreak: deadlineRateLimitStreakFor(attemptState, ticket.id),
    decisionAuto: input.decisionAuto,
    // 06h R3 (B3): one DI path for the signal flag — never the global here.
    interruptReceived: input.isInterrupted,
  });

  // Post-route: promote review report, persist retry/backoff state, journal
  // attempt_finished, cost accounting — supervisor-loop-post-route-
  // finalize.ts (split, bước 0b phase 03). Same behavior, moved verbatim.
  const { attemptFinishedSeq } = await finalizeAttemptAfterRoute({
    tickInput: input,
    ticket,
    cycle,
    route,
    attemptState: withDeadlineRateLimitStreakAfterRoute(attemptState, ticket.id, cycle, route),
    attemptNo,
    priorRetryState,
    priorBackoffIndex,
    attemptStartedAt,
  });
  // 06d R7 (F-17, E20): attempt_finished is journaled; archive + reset a
  // non-done attempt so the next `run`'s bootstrap commit cannot sweep it in.
  await resetWorktreeAfterNonDoneRoute({
    cycle,
    route,
    controlRoot: input.controlRoot,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    ticketId: ticket.id,
    attemptNo,
    notify: input.notify,
  });
  // Recovery (D2.7): after usage (AC6) AND the reset (audit M-1), never in the stopped worker's leftovers.
  const recovery = await materializeRecoveryAfterStop(input, route, attemptFinishedSeq);

  return { kind: "attempted", ticketId: ticket.id, route, ...recovery };
}
